import React, { useEffect, useMemo, useState } from "react";
import ReactWeather from "react-open-weather";
import { useDashboardHook } from "../hooks/useDashboardHooks";
import axios from "axios";
import svgIcons from "react-open-weather/src/js/svgIcons";
import { getCenter } from "../util";
import { VisualCrossKey } from "src/config/secret";
import { Spinner } from "reactstrap";
import tzlookup from "tz-lookup";

const mapToIcon: { [key: string]: string } = {
  "clear-day": "sunny",
  "clear-night": "sunny",
  cloudy: "cloudy",
  "partly-cloudy-day": "cloudy",
  "partly-cloudy-night": "cloudy",
  wind: "cloudy",
  rain: "rain",
  fog: "fog",
  snow: "snow",
  "showers-day": "showers",
  "showers-night": "showers",
  "snow-showers-day": "snow",
  "snow-showers-night": "snow",
  "thunder-rain": "thunderstorms",
  "thunder-showers-day": "thunderstorms",
  "thunder-showers-night": "thunderstorms",
};

const VisualCrossingURL =
  "https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline";

const formatDateInTimeZone = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const copy = new Date(date);
  copy.setUTCDate(copy.getUTCDate() + days);
  return copy;
};

const WeatherTable = () => {
  const { chosenField } = useDashboardHook();

  const [data, setData] = useState<unknown>();
  const [location, setLocation] = useState("");
  const [isFetching, setFetching] = useState(false);

  const center = useMemo(
    () => (chosenField.coordinates ? getCenter(chosenField.coordinates) : null),
    [chosenField]
  );

  useEffect(() => {
    if (!center) return;
    const source = axios.CancelToken.source();
    const fetchWeather = async () => {
      try {
        setFetching(true);
        setData(undefined)

        // let today = new Date();
        // let startDate = `${today.getFullYear()}-${today.getMonth() + 1
        //   }-${today.getDate()}`;
        //
        // let fourthDay = new Date();
        // fourthDay.setDate(today.getDate() + 4);
        // let endDate = `${fourthDay.getFullYear()}-${fourthDay.getMonth() + 1
        //   }-${fourthDay.getDate()}`;
        // Fix by Maria:
        const fieldTimezone = tzlookup(center.lat, center.lng);
        const today = new Date();
        const startDate = formatDateInTimeZone(today, fieldTimezone);
        const endDate = formatDateInTimeZone(addDays(today, 4), fieldTimezone);

        // TODO: fetch weather according to farm or field location
        let res = await axios.get(
          `${VisualCrossingURL}/${center.lat.toString()},${center.lng.toString()}/${startDate}/${endDate}`,
          {
            params: {
              unitGroup: "uk",
              key: VisualCrossKey,
            },
            cancelToken: source.token,
          }
        );
        const { days, currentConditions } = res.data;
        // format the forcast weather data
        const forecast = days.map(
          (day: { icon: string;[key: string]: any }) => ({
            date: new Date(day.datetime).toDateString(),
            description: day.conditions,
            icon: svgIcons[mapToIcon[day.icon]],
            temperature: {
              min: Math.round(day.tempmin),
              max: Math.round(day.tempmax),
            },
            wind: Math.round(day.windspeed * 1.60934 * 10) / 10, // miles/h to km/h, 1 decimal place
            humidity: day.humidity,
          })
        );
        // format the current weather data
        let current = forecast[0];
        current.temperature.current = Math.round(currentConditions?.temp ?? days[0].temp);
        //current.temperature.current = Math.round(currentConditions.temp);
        const data = {
          forecast,
          current,
        };

        setData(data);
        setLocation(res.data.timezone.split("/")[1]);
        setFetching(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setFetching(false);
          console.log(err)
        }
      }
    };

    fetchWeather();
    return () => {
      setFetching(false)
      source.cancel();
    }
  }, [center]);

  return isFetching ? (
    <div className="d-flex flex-column h-100 justify-content-center ">
      <Spinner className="mx-auto d-block" />
    </div>
  ) : (
    <ReactWeather isLoading={isFetching} data={data} locationLabel={location} />
  );
};

export default WeatherTable;
